import {
	ArrowLeft,
	BadgeCheck,
	Copy,
	Phone,
	QrCode,
	Share2,
	Shield,
	Star
} from "lucide-react";
import { useState } from "react";

import { useVendor } from "@/api/vendor-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ProfileHeader from "@/components/vendor/profile-header";
import { createFileRoute, useRouter } from "@tanstack/react-router";

export const Route = createFileRoute("/buyer/vendor/$vendorid")({
	component: VendorProfile
});

export default function VendorProfile() {
	const { vendorid } = Route.useParams();
	const { data } = useVendor(vendorid);

	const { history } = useRouter();
	const [rating, setRating] = useState<number>(0);

	return (
		<div className="mx-auto w-full p-4]">
			<header className="mb-6 flex items-center justify-between">
				<Button onClick={() => history.back()} variant="secondary" className="gap-2 bg-white mb-4">
					<ArrowLeft className="h-4 w-4" />
					<span>Back</span>
				</Button>
			</header>

			<Card className="shadow-none min-h-[80vh]">
				<CardHeader className="flex-row flex-wrap items-start gap-4 space-y-0">
					<ProfileHeader imageUrl={data?.data.profileImage ?? "/placeholder.svg"} name={data?.data.fullName ?? ""} />
					<div className="flex-1">
						<div className="flex items-center gap-2">
							<h1 className="text-2xl font-bold">{data?.data.fullName}</h1>
							<BadgeCheck className="h-5 w-5 text-primary" />
						</div>
						<p className="text-sm text-muted-foreground">
							62 buyers rated this vendor
						</p>
					</div>
					<div className="flex gap-4">
						<Button variant="secondary" size="icon" className="rounded-full">
							<Copy className="h-5 w-5" />
						</Button>
						<Button variant="secondary" size="icon" className="rounded-full">
							<QrCode className="h-5 w-5" />
						</Button>
						<Button variant="secondary" size="icon" className="rounded-full bg-red-100 text-destructive">
							<Shield className="h-5 w-5" />
						</Button>
					</div>
				</CardHeader>

				<CardContent className="space-y-6">
					<div className="flex flex-wrap gap-2">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" className="flex items-center gap-2 *:text-primary *:font-thin">
									<span>View socials</span>
									<ArrowLeft className="h-4 w-4 rotate-[-90deg]" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent>
								<DropdownMenuItem>
									<a href="https://facebook.com" target="_blank" rel="noopener noreferrer">
										Facebook
									</a>
								</DropdownMenuItem>
								<DropdownMenuItem>
									<a href="https://twitter.com" target="_blank" rel="noopener noreferrer">
										Twitter
									</a>
								</DropdownMenuItem>
								<DropdownMenuItem>
									<a href="https://instagram.com" target="_blank" rel="noopener noreferrer">
										Instagram
									</a>
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
						<Button variant="outline" className="flex items-center gap-2 *:text-primary *:font-thin">
							<Phone className="h-4 w-4" />
							<span>Call this vendor</span>
						</Button>
						<Button variant="outline" className="flex items-center gap-2 *:text-primary *:font-thin">
							<Star className="h-4 w-4" />
							<span>Add to favorites</span>
						</Button>
						<Button variant="outline" className="flex items-center gap-2 *:text-primary *:font-thin">
							<Share2 className="h-4 w-4" />
							<span>Share profile</span>
						</Button>
						<Button variant="outline" className="flex items-center gap-2 *:text-primary *:font-thin">
							<Shield className="h-4 w-4" />
							<span>Report profile</span>
						</Button>
					</div>

					<div className="grid gap-6 md:grid-cols-2">
						<div className="flex flex-col justify-center space-y-1 border rounded-lg px-4 py-1">
							<p className="text-sm text-muted-foreground">Phone Number</p>
							<p className="font-medium">{data?.data.phoneNumber ?? "xxx-xxx-xxx"}</p>
						</div>
						<div className="flex flex-col justify-center space-y-1 border rounded-lg px-4 py-1">
							<p className="text-sm text-muted-foreground">Location</p>
							<p className="font-medium">{data?.data.address ?? ""}</p>
						</div>
						<div className="flex flex-col justify-center space-y-1 border rounded-lg px-4 py-1">
							<p className="text-sm text-muted-foreground">Email address</p>
							<p className="font-medium">{data?.data.email}</p>
						</div>
						<div className="flex flex-col justify-center space-y-1 border rounded-lg px-4 py-1">
							<p className="text-sm text-muted-foreground">Website</p>
							<Button
								variant="link"
								className="p-0 justify-start"
							>
								<a href="https://bellashoes.bumpa.com">https://www.change.com</a>
							</Button>
						</div>
					</div>

					<div className="space-y-2">
						<p className="font-medium">Tap to rate</p>
						<div className="flex items-center gap-2">
							{[1, 2, 3, 4, 5].map(star => (
								<Button
									key={star}
									variant="ghost"
									size="icon"
									onClick={() => setRating(star)}
									className={
										star <= rating ? "text-yellow-400" : "text-muted-foreground"
									}
								>
									<Star
										className="h-6 w-6"
										fill={star <= rating ? "currentColor" : "none"}
									/>
								</Button>
							))}
							<span className="ml-2 text-sm text-muted-foreground">
								62 ratings
							</span>
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
